import React, {createContext, useContext, useState, useMemo, useCallback} from "react";
import api from "../Api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState({ isAuthenticated: false, role: null });
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const response = await api.get("/users/check-auth");
            setAuth({
                userId: response.data.userID,
                isAuthenticated: response.data.loggedIn,
                role: response.data.role,
            });
        } catch {
            setAuth({ isAuthenticated: false, role: null });
        } finally {
            setLoading(false);
        }
    }, []); // ✅ Stable function reference

    const login = useCallback(async (credentials) => {
        await api.post("/users/login", credentials);
        await checkAuth();
    }, [checkAuth]);

    const logout = useCallback(async () => {
        await api.post("/users/logout");
        setAuth({ isAuthenticated: false, role: null });
    }, []);

    // ✅ Memoize context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        auth,
        login,
        logout,
        checkAuth,
        loading
    }), [auth, login, logout, checkAuth, loading]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
